import React, { useEffect, useState } from "react";
import { Modal, message, Form, Input, Menu } from "antd";
import { $getRoot } from "lexical";
import { saveAs } from "file-saver";
import { useSelector } from "react-redux";
import { $generateHtmlFromNodes } from "@lexical/html";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, ImageRun, Table, TableRow, TableCell, ShadingType, WidthType, HeightRule } from "docx";
import { exportEditorToDocx } from "./docxExportUtils";
import "./style.css";

const ExportMenu = ({
    setFileMenuVisible,
    isExportModalVisible,
    setIsExportModalVisible,
    selectedExportType,
    setSelectedExportType,
}) => {
    const editor = useSelector((state) => state.editor.lexicalEditor);
    const documentInformation = useSelector((state) => state.editor.documentInformation);
    const [form] = Form.useForm();
    const [isExporting, setIsExporting] = useState(false);
    const exportFileName = documentInformation?.name || "Untitled document";
    
    // Detect theme from document or user preference
    const [currentTheme, setCurrentTheme] = useState('light');

    useEffect(() => {
        if (isExportModalVisible && exportFileName) {
            form.setFieldsValue({ fileName: exportFileName });
        }
    }, [isExportModalVisible, exportFileName]);

    useEffect(() => {
        // Detect dark mode
        const isDarkMode = document.documentElement.classList.contains('dark') || 
                          document.body.classList.contains('dark-theme') ||
                          window.matchMedia('(prefers-color-scheme: dark)').matches;
        setCurrentTheme(isDarkMode ? 'dark' : 'light');
    }, []);

    const resetExportState = () => {
        setSelectedExportType("");
        setIsExportModalVisible(false);
        form.resetFields();
    };

    const handleMenuClick = (e) => {
        const { key } = e;
        setSelectedExportType(key);
        form.setFieldsValue({ fileName: exportFileName });
        setFileMenuVisible(false);
        setIsExportModalVisible(true);
    };

    const handleExport = () => {
        if (!selectedExportType) return;

        form.validateFields().then(async (values) => {
            const fileName = values.fileName || exportFileName;
            setIsExporting(true);

            try {
                // Special handling for DOCX since it needs async processing
                if (selectedExportType === "docx") {
                    await exportAsDOCX(fileName);
                } else if (selectedExportType === "pdf") {
                    await exportAsPDF(fileName);
                } else {
                    // Synchronous exports
                    editor.getEditorState().read(() => {
                        const editorContent = editor.getEditorState().toJSON();

                        switch (selectedExportType) {
                            case "pad":
                                exportAsLexical(editorContent, fileName);
                                break;
                            case "html":
                                exportAsHTML(fileName);
                                break;
                            case "txt":
                                exportAsText(fileName);
                                break;
                            default:
                                console.error("Invalid export format selected.");
                        }
                    });
                }

                resetExportState();
                setFileMenuVisible(false);
            } catch (error) {
                console.error("Export failed:", error);
                message.error(`Failed to export as ${selectedExportType.toUpperCase()}. Please try again.`);
            } finally {
                setIsExporting(false);
            }
        });
    };

    const exportAsLexical = (content, fileName) => {
        const blob = new Blob([JSON.stringify(content, null, 2)], {
            type: "application/json",
        });
        saveAs(blob, `${fileName}.pad`);
        message.success(`File successfully exported as ${fileName}.pad`);
    };

    const exportAsHTML = (fileName) => {
        const htmlString = generateHtmlFromEditorState();
        const blob = new Blob([htmlString], { type: "text/html" });
        saveAs(blob, `${fileName}.html`);
        message.success(`File successfully exported as ${fileName}.html`);
    };

    const generateHtmlFromEditorState = () => {
        const container = document.createElement("div");
        const htmlNodes = $generateHtmlFromNodes(editor, null);
        container.innerHTML = htmlNodes;
        return container.innerHTML;
    };

    const exportAsText = (fileName) => {
        const stringifiedEditorState = JSON.stringify(editor.getEditorState().toJSON());
        const parsedEditorState = editor.parseEditorState(stringifiedEditorState);
        const textContent = parsedEditorState.read(() => $getRoot().getTextContent());
        const blob = new Blob([textContent], { type: "text/plain" });
        saveAs(blob, `${fileName}.txt`);
        message.success(`File successfully exported as ${fileName}.txt`);
    };

    const exportAsPDF = async (fileName) => {
        try {
            const { jsPDF } = await import("jspdf");
            const doc = new jsPDF();

            editor.update(() => {
                const htmlContent = generateHtmlFromEditorState();
                doc.html(htmlContent, {
                    callback: (doc) => {
                        doc.save(`${fileName}.pdf`);
                        message.success(`File successfully exported as ${fileName}.pdf`);
                    },
                    x: 10,
                    y: 10,
                    width: 190,
                    windowWidth: 650,
                });
            });
        } catch (error) {
            console.error("Error exporting PDF:", error);
            message.error("Failed to export PDF.");
        }
    };

    const exportAsDOCX = async (fileName) => {
        try {
            message.loading({ content: 'Generating DOCX file...', key: 'docx-export', duration: 0 });
            
            const options = {
                theme: currentTheme,
                pageSize: 'LETTER',
            };

            const blob = await exportEditorToDocx(editor, fileName, options);
            
            if (!blob || blob.size === 0) {
                throw new Error('Generated DOCX file is empty');
            }
            
            saveAs(blob, `${fileName}.docx`);
            
            message.success({ 
                content: `File successfully exported as ${fileName}.docx`, 
                key: 'docx-export',
                duration: 3
            });
        } catch (error) {
            console.error("Error exporting DOCX:", error);
            message.error({ 
                content: `Failed to export DOCX: ${error.message || 'Please try again.'}`, 
                key: 'docx-export',
                duration: 5
            });
            throw error;
        }
    };



    const handleCancel = () => {
        resetExportState();
    };

    return (
        <>
            <Menu onClick={handleMenuClick} selectedKeys={[]} className="export-menu-item-container">
                {[
                    { key: "docx", label: "Microsoft Word (.docx)", description: "" },
                    { key: "pdf", label: "PDF Document (.pdf)", description: "" },
                    { key: "html", label: "Web Page (.html)", description: "" },
                    { key: "txt", label: "Plain Text (.txt)", description: "" },
                    { key: "pad", label: "Pad (.pad)", description: "" },
                ].map((format) => (
                    <Menu.Item key={format.key} className="export-menu-item">
                        <span className="file-menu-item">
                            <span className="file-menu-item-left" />
                            <div className="file-menu-item-content">
                                <span className="file-menu-item-text">{format.label}</span>
                                {format.description && (
                                    <span className="file-menu-item-description">{format.description}</span>
                                )}
                            </div>
                        </span>
                    </Menu.Item>
                ))}
            </Menu>

            <Modal
                title="Export Document"
                visible={isExportModalVisible}
                onOk={handleExport}
                onCancel={handleCancel}
                afterClose={() => form.resetFields()}
                okText="Download"
                cancelText="Cancel"
                confirmLoading={isExporting}
                okButtonProps={{ 
                    disabled: isExporting,
                    className: 'export-modal-btn-download'
                }}
                cancelButtonProps={{
                    className: 'export-modal-btn-cancel'
                }}
                className="export-document-modal"
                centered
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="File Name"
                        name="fileName"
                        rules={[
                            { required: true, message: "Please enter a file name." },
                            {
                                pattern: /^[^<>:;,?"*|/\\]+$/,
                                message: "File name contains invalid characters.",
                            },
                        ]}
                    >
                        <Input 
                            placeholder="Enter file name"
                            disabled={isExporting}
                            className="export-modal-input"
                        />
                    </Form.Item>
                    {selectedExportType && (
                        <div className="export-format-info">
                            <div className="export-format-label">
                                Export format: <strong>.{selectedExportType}</strong>
                            </div>
                            {selectedExportType === 'docx' && (
                                <div className="export-format-description">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    Preserves formatting, images, tables, and styles
                                </div>
                            )}
                        </div>
                    )}
                </Form>
            </Modal>
        </>
    );
};

export default React.memo(ExportMenu);